import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Fired on `window` when an instance with `syncAcrossInstances` is dismissed (or
 *  `dismissBannerByKey` is called), so every mounted instance with the same key that also opted in
 *  hides at once instead of on the next page load. `detail` is the computed key
 *  (storageKey + userId). */
export const DISMISSIBLE_BANNER_DISMISSED_EVENT =
  "suno:dismissible-banner-dismissed";

// Module-level set prevents duplicate "shown" Datadog events across remounts.
// Keyed by the computed storage key (storageKey + userId).
const _shownThisSession = new Set<string>();

interface DismissibleBannerState {
  firstSeenAt: string;
  dismissed?: boolean;
  dismissedAt?: string;
}

function computeInitialVisibility(
  storage: ReturnType<typeof setupLocalStorageAPI>,
  ttlMs: number
): boolean {
  const state = storage.getData(null) as DismissibleBannerState | null;
  if (!state) {
    storage.updateData({ firstSeenAt: new Date().toISOString() });
    return true;
  }
  if (state.dismissed) return false;
  if (Date.now() - new Date(state.firstSeenAt).getTime() > ttlMs) return false;
  return true;
}

export interface UseDismissibleBannerOptions {
  /** Stable, app-unique prefix. Convention: "suno_<feature>_v<N>_"
   *  Bump the version suffix to reset dismissal state on redeployment. */
  storageKey: string;
  /** Authenticated user ID. Only render the consuming component when this is available —
   *  do NOT pass undefined; render the banner conditionally in the parent instead. */
  userId: string;
  /** Auto-expire TTL in ms from first-seen. Defaults to 30 days. */
  ttlMs?: number;
  /** When false, skip localStorage entirely and return visible=false. Useful when a
   *  feature flag or auth state gates the banner. Defaults to true. */
  enabled?: boolean;
  /** Hide at once when another instance with the same key is dismissed, and tell the others when
   *  this one is. Off by default: without it, instances sharing a key only agree on the next page
   *  load (e.g. the audiogram comparison banner and its header chip). */
  syncAcrossInstances?: boolean;
  /** Optional Datadog RUM action names. */
  datadogActions?: {
    /** Fired once per browser session the first time the banner becomes visible. */
    shown?: string;
    /** Fired each time the banner is dismissed. Context passed to dismiss() is forwarded. */
    dismissed?: string;
  };
}

export interface UseDismissibleBannerReturn {
  visible: boolean;
  /** Call when the user (or code) dismisses the banner.
   *  Pass an optional context object — it is forwarded as Datadog action attributes.
   *  Example: dismiss({ source: "banner_got_it" }) */
  dismiss: (context?: Record<string, unknown>) => void;
}

export function useDismissibleBanner({
  storageKey,
  userId,
  ttlMs = DEFAULT_TTL_MS,
  enabled = true,
  syncAcrossInstances = false,
  datadogActions
}: UseDismissibleBannerOptions): UseDismissibleBannerReturn {
  const computedKey = `${storageKey}${userId}`;

  const storage = useMemo(
    () => (enabled ? setupLocalStorageAPI(computedKey) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [computedKey, enabled]
  );

  const [visible, setVisible] = useState(() => {
    if (!storage) return false;
    return computeInitialVisibility(storage, ttlMs);
  });

  // Capture datadogActions in a ref so the effect below doesn't need them as deps.
  const actionsRef = useRef(datadogActions);
  actionsRef.current = datadogActions;

  useEffect(() => {
    if (!visible || !actionsRef.current?.shown) return;
    if (_shownThisSession.has(computedKey)) return;
    _shownThisSession.add(computedKey);
    datadogRum.addAction(actionsRef.current.shown, {});
  }, [visible, computedKey]);

  useEffect(() => {
    if (!syncAcrossInstances) return undefined;
    const onDismissed = (event: Event) => {
      if ((event as CustomEvent<string>).detail === computedKey) {
        setVisible(false);
      }
    };
    window.addEventListener(DISMISSIBLE_BANNER_DISMISSED_EVENT, onDismissed);
    return () =>
      window.removeEventListener(
        DISMISSIBLE_BANNER_DISMISSED_EVENT,
        onDismissed
      );
  }, [computedKey, syncAcrossInstances]);

  const dismiss = useCallback(
    (context?: Record<string, unknown>) => {
      if (!storage) return;
      const existing = storage.getData(null) as DismissibleBannerState | null;
      storage.updateData({
        firstSeenAt: existing?.firstSeenAt ?? new Date().toISOString(),
        dismissed: true,
        dismissedAt: new Date().toISOString()
      });
      setVisible(false);
      if (syncAcrossInstances) {
        window.dispatchEvent(
          new CustomEvent(DISMISSIBLE_BANNER_DISMISSED_EVENT, {
            detail: computedKey
          })
        );
      }
      if (actionsRef.current?.dismissed) {
        datadogRum.addAction(actionsRef.current.dismissed, context ?? {});
      }
    },
    [storage, computedKey, syncAcrossInstances]
  );

  return { visible, dismiss };
}

/**
 * Dismiss a banner from code that does not render it (e.g. "the person just used the feature").
 * Writes the same dismissed state the hook does and hides mounted instances that opted into
 * `syncAcrossInstances`. The Datadog `dismissed` action fires only if the banner had been shown
 * and had not expired (default TTL), so a banner nobody saw is never counted as dismissed; it is still marked
 * dismissed so it never appears afterwards.
 */
export function dismissBannerByKey({
  storageKey,
  userId,
  datadogAction,
  context
}: {
  storageKey: string;
  userId: string;
  datadogAction?: string;
  context?: Record<string, unknown>;
}) {
  const computedKey = `${storageKey}${userId}`;
  const storage = setupLocalStorageAPI(computedKey);
  const existing = storage.getData(null) as DismissibleBannerState | null;
  if (existing?.dismissed) return;
  storage.updateData({
    firstSeenAt: existing?.firstSeenAt ?? new Date().toISOString(),
    dismissed: true,
    dismissedAt: new Date().toISOString()
  });
  window.dispatchEvent(
    new CustomEvent(DISMISSIBLE_BANNER_DISMISSED_EVENT, { detail: computedKey })
  );
  const wasShowing =
    existing &&
    Date.now() - new Date(existing.firstSeenAt).getTime() <= DEFAULT_TTL_MS;
  if (wasShowing && datadogAction) {
    datadogRum.addAction(datadogAction, context ?? {});
  }
}
